/**
 * The row windows that are currently on screen.
 *
 * Realtime row events name a table, not a view, and they arrive outside React
 * entirely -- the socket is a module singleton created before the router. So
 * something has to connect an event about table 12 to whichever windows are
 * showing table 12 right now, and a module-level registry is the smallest thing
 * that can: a component registers while it is mounted and unregisters when it
 * is not.
 *
 * This is the seam the plan reserved when it said the row buffer is not
 * TanStack Query. Everything else the socket touches is a cache entry it can
 * reach through the `QueryClient`; the rows are a live object owned by a
 * component, and this is how they are reached.
 *
 * A **set** per table, not one entry: a board is one window per stack, and each
 * decides for itself whether a row belongs in it, because each has different
 * filters.
 */
import type { Field } from '@/api/types'
import type { RowLifecycleContext } from './rowLifecycle'
import type { FilterableView } from './rowMatch'
import type { OrderTerm } from './rowSort'
import type { RowWindowStore } from './rowWindowStore'

/** One mounted window, and everything a row event needs to decide about it. */
export interface RowWindowMember {
  tableId: number
  viewId: number
  store: RowWindowStore
  /**
   * Read at the moment an event arrives rather than captured when the window
   * registered. Fields and filters change under a mounted grid, and a decision
   * taken with the schema as it was when the page loaded is a decision about a
   * table that no longer exists.
   */
  fields: () => readonly Field[]
  view: () => FilterableView & { sortings?: readonly OrderTerm[] }
  groupBys: () => readonly OrderTerm[]
}

const members = new Set<RowWindowMember>()

/** Registers a window and returns the function that removes it. */
export function registerRowWindow(member: RowWindowMember): () => void {
  members.add(member)
  return () => {
    members.delete(member)
  }
}

export function rowWindowsForTable(tableId: number): RowWindowMember[] {
  return [...members].filter((member) => member.tableId === tableId)
}

export function rowWindowsForView(viewId: number): RowWindowMember[] {
  return [...members].filter((member) => member.viewId === viewId)
}

/** Test seam: a page with nothing on it. */
export function __resetRowWindowsForTests(): void {
  members.clear()
}

/**
 * The context a lifecycle decision needs, built from one mounted window.
 *
 * `rowsForComparison` is the window's own rows and nothing else. A row is
 * ordered against what is held rather than against the table, which is why
 * `matchSortings` is a statement about the loaded slice -- there is nothing else
 * to compare against, and claiming otherwise would badge every row the reader
 * has not scrolled to.
 *
 * Shared by the realtime handlers and by this tab's own edits, because "does
 * this row still belong here, and where" is the same question however the row
 * changed.
 */
export function lifecycleContextFor(member: RowWindowMember): RowLifecycleContext {
  return {
    view: member.view(),
    fields: member.fields(),
    groupBys: member.groupBys(),
    rowsForComparison: () => member.store.getState().window.rows,
  }
}
